import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConsentDocument } from '@/api/consentDocuments/consentDocumentsApi.types';
import { useConsentDocumentsQuery } from '@/hooks/useConsentDocuments';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/common/Icon';
import { ConsentDocumentEditor } from './ConsentDocumentEditor';
import { resolveActiveLanguageTitle } from './consentDocumentTitle';

type EditorState = { mode: 'list' } | { mode: 'create' } | { mode: 'edit'; documentId: string };

export function ConsentDocumentsSection() {
  const { t, i18n } = useTranslation('settings');
  const documentsQuery = useConsentDocumentsQuery();
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'list' });

  if (documentsQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (documentsQuery.isError === true || documentsQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          documentsQuery.refetch();
        }}
      />
    );
  }

  const documents = documentsQuery.data;

  if (editorState.mode !== 'list') {
    return (
      <ConsentDocumentEditor
        documentId={editorState.mode === 'edit' ? editorState.documentId : null}
        sortOrderForNew={documents.length}
        onClose={function backToList() {
          setEditorState({ mode: 'list' });
        }}
      />
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-xs text-zoomuted">{t('consentDocuments.settingsNote')}</p>
      <Card padding="md">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h2 className="text-sm font-bold text-zooindigo">{t('consentDocuments.settingsTitle')}</h2>
          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={function openCreate() {
              setEditorState({ mode: 'create' });
            }}
          >
            <Icon name="Plus" /> {t('consentDocuments.add')}
          </Button>
        </div>

        {documents.length === 0 ? (
          <p className="text-sm text-zoomuted py-2">{t('consentDocuments.empty')}</p>
        ) : (
          <div className="divide-y divide-zooborder">
            {documents.map(function renderRow(document) {
              return (
                <ConsentDocumentRow
                  key={document.id}
                  document={document}
                  language={i18n.language}
                  onOpen={function openEdit() {
                    setEditorState({ mode: 'edit', documentId: document.id });
                  }}
                />
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

interface ConsentDocumentRowProps {
  document: ConsentDocument;
  language: string;
  onOpen: () => void;
}

function ConsentDocumentRow({ document, language, onOpen }: ConsentDocumentRowProps) {
  const { t } = useTranslation('settings');
  const title = resolveActiveLanguageTitle(document.currentVersion, language);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 py-3 text-left transition-colors hover:bg-zoovpalelavender hover:bg-opacity-30 px-1 rounded-lg"
    >
      <Icon name="FileText" className="text-zooprimary shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-zooindigo truncate">{title}</span>
        <span className="block text-xs text-zoomuted">
          {t('consentDocuments.versionLabel', { version: document.currentVersion.version })}
        </span>
      </span>
      <Badge
        tone={document.isRequired === true ? 'warning' : 'muted'}
        shape="tag"
        size="sm"
        uppercase
        className="text-[10px] px-1.5"
      >
        {document.isRequired === true
          ? t('consentDocuments.required')
          : t('consentDocuments.optional')}
      </Badge>
      <Icon name="ChevronRight" className="text-zoomuted shrink-0" />
    </button>
  );
}
